import { DateTime } from 'luxon'
import { v4 as uuid } from 'uuid'
import { Form } from 'src/app/state-man/models/form.model'
import type { IsoDate } from './dates'
import type { FormCode } from './form-catalog'
import type { FormItem } from './paper'
import { LEGACY_STATUS_NAME, STATUS_ID_FOR } from './status'

/* Duplicate and Add Another on a form card both save a new Draft segment of the chain right there on the
 * list, without opening the editor:
 *   duplicate  → every field of the source form is copied; the tech reviews it and submits.
 *   addAnother → only the job & equipment fields carry over (the same keys the editors carry when they build
 *                a new segment), so the next unit starts from a mostly blank sheet. */
export type CopyHow = 'duplicate' | 'addAnother'

/** Keys every segment gets fresh: the sheet's own number and who is filling it in. */
const NEVER_CARRIED: readonly string[] = ['device_number', 'lead_technician_name']

const oneOf = (keys: readonly string[]) => (key: string) => keys.includes(key)

/** form_data keys Add Another carries forward, per form type. Keep in step with each editor's getExtendedFormObj. */
const CARRIED: { readonly [K in FormCode]?: (key: string) => boolean } = {
  AVI: oneOf(['manufacturer', 'model', 'inlet', 'outlet', 'type', 'repairable', 'lube_yes_no', 'type_of_grease', 'body', 'seat', 'seal', 'stem', 'mop', 'class', 'type_of_valve']),
  CIR: key => ['inspection_type', 'crane_type', 'manufacturer', 'model', 'serial_number'].includes(key)
    || /^wr_[a-z]+_(type|grade)$/.test(key)
    || /^(main|aux)_(mfr|serial|capacity|weight)$/.test(key),
  CMC: key => ['compressor_cylinders', 'crank_throws'].includes(key) || key.startsWith('equip_'),
  CME: key => ['cylinders_per_bank', 'crank_throws', 'vr_hot', 'vr_cold'].includes(key) || key.startsWith('equip_'),
  THI: oneOf(['th_brand', 'th_model_number', 'th_serial_number', 'th_pressure', 'pressure_gasket', 'pressure_pallet', 'vacuum_gasket', 'vacuum_pallet', 'seating_area', 'base_gasket', 'lid_hatch']),
  TOF: oneOf(['manufacturer', 'model', 'uv_code_stamp', 'inlet', 'outlet', 'type', 'orifice', 'valve_cdtp', 'scfm_gpm', 'scfm_gpm_type', 'medium']),
  VVR: oneOf(['valve_brand', 'valve_model_number', 'valve_serial_number', 'valve_pressure', 'valve_size', 'pressure_gasket', 'pressure_pallet', 'vacuum_gasket', 'vacuum_pallet', 'seating_area', 'base_gasket', 'lid_hatch']),
}

function dataOf(form: Form): Record<string, unknown> {
  const raw: unknown = form.form_data
  if (raw && typeof raw === 'object') return raw as Record<string, unknown>
  if (typeof raw === 'string') {
    try {
      const parsed: unknown = JSON.parse(raw)
      if (parsed && typeof parsed === 'object') return parsed as Record<string, unknown>
    } catch { /* not JSON: start blank */ }
  }
  return {}
}

/** The form_data of the new segment, before its own number and technician are set. */
export function copiedData(code: FormCode | null, source: Record<string, unknown>, how: CopyHow): Record<string, unknown> {
  const keep = how === 'duplicate' ? () => true : (code && CARRIED[code]) ?? (() => false)
  const out: Record<string, unknown> = {}
  for (const key of Object.keys(source)) if (!NEVER_CARRIED.includes(key) && keep(key)) out[key] = source[key]
  return out
}

export type CopiedFrom = Form['copied_from'] | undefined

/** The line under a copied Draft, on its card and above its sheet. */
export function copyNote(copied: CopiedFrom): string | null {
  if (!copied) return null
  return copied.how === 'duplicate'
    ? `Duplicate of ${copied.number} · all fields copied — review before submitting.`
    : `Next in line after ${copied.number} · job & equipment fields carried over.`
}

/**
 * The next segment of `source`'s chain as a Draft saved on the device, mirroring what the editor's
 * persist('inProgress') would have written: same job, next group_number, pending upload.
 */
export function copyForm(source: FormItem, how: CopyHow, user: { readonly id: number; readonly name: string }, today: IsoDate): Form {
  const ref = source.ref
  const deviceNumber = Form.formatDeviceNumber(source.nextSegment, source.woNumber)
  return Form.buildOne({
    uuid: uuid(), id: 0,
    group_uuid: source.groupUuid, group_number: source.nextSegment, form_identifier: deviceNumber,
    work_order_id: ref.work_order_id, work_order_number: source.woNumber,
    company_name: ref.company_name, area: ref.area, location_name: ref.location_name, unit_number: ref.unit_number, job_type: ref.job_type,
    form_type_id: ref.form_type_id, form_name: ref.form_name,
    author_id: user.id, author: user.name,
    created: DateTime.now().toISO(), date_worked: source.worked, submitted_at: today,
    form_status_id: STATUS_ID_FOR.form.inProgress, status: LEGACY_STATUS_NAME.inProgress,
    is_pending_save: true, is_pending_delete: false,
    form_data: { ...copiedData(source.code, dataOf(ref), how), device_number: deviceNumber, lead_technician_name: user.name },
    form_photos: [], sales_order_items: [],
    copied_from: { number: source.number, how },
  })
}
